import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Store as StoreIcon, CheckCircle2, PauseCircle, Unlink, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatCard } from "@/components/kit/StatCard";
import { SearchInput } from "@/components/kit/SearchInput";
import { FilterShell } from "@/components/kit/FilterShell";
import { StatusBadge } from "@/components/kit/StatusBadge";
import { Chip } from "@/components/kit/Chip";
import { DataTable } from "@/components/tables/DataTable";
import { RowActions } from "@/components/tables/RowActions";
import { EmptyState } from "@/components/feedback/EmptyState";
import { TableSkeleton } from "@/components/feedback/Skeletons";
import { ConfirmDialog } from "@/components/feedback/ConfirmDialog";
import { ExportButton } from "@/components/kit/ExportButton";
import { StoreDrawer } from "./StoreDrawer";
import { storesService, clientsService, vaAgenciesService, usersService } from "@/services/resourceServices";
import { formatApiErrorDetail } from "@/services/api";
import { formatRelativeTime } from "@/lib/format";
import { MARKETPLACES } from "@/lib/constants";
import { useAuth } from "@/hooks/useAuth";
import { canWrite } from "@/lib/permissions/permissions";

const DEFAULT_FILTERS = { search: "", status: "all", marketplace: "all", client: "all" };

export default function StoresPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const writable = canWrite(user?.role, "stores");
  const [draft, setDraft] = useState(DEFAULT_FILTERS);
  const [applied, setApplied] = useState(DEFAULT_FILTERS);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const params = useMemo(
    () => ({
      search: applied.search,
      status: applied.status === "all" ? "" : applied.status,
      marketplace: applied.marketplace === "all" ? "" : applied.marketplace,
      client_id: applied.client === "all" ? "" : applied.client,
    }),
    [applied]
  );

  const listQuery = useQuery({ queryKey: ["stores", params], queryFn: () => storesService.list(params) });
  const summaryQuery = useQuery({ queryKey: ["stores", "summary"], queryFn: storesService.summary });
  const clientsQuery = useQuery({ queryKey: ["clients", "lite"], queryFn: () => clientsService.list(), enabled: writable });
  const agenciesQuery = useQuery({ queryKey: ["va-agencies", "lite"], queryFn: () => vaAgenciesService.list(), enabled: writable });
  const staffQuery = useQuery({ queryKey: ["users", "va-staff"], queryFn: () => usersService.list({ role: "VA_STAFF" }), enabled: writable });

  const clients = useMemo(() => clientsQuery.data?.items || [], [clientsQuery.data]);
  const agencies = useMemo(() => agenciesQuery.data?.items || [], [agenciesQuery.data]);
  const vaStaff = staffQuery.data?.items || [];
  const clientMap = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c.client_name])), [clients]);
  const agencyMap = useMemo(() => Object.fromEntries(agencies.map((a) => [a.id, a.name])), [agencies]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["stores"] });
    qc.invalidateQueries({ queryKey: ["activity"] });
  };

  const openCreate = () => { setEditing(null); setDrawerOpen(true); };
  const openEdit = (row) => { setEditing(row); setDrawerOpen(true); };

  const confirmDelete = async () => {
    try {
      await storesService.remove(toDelete.id);
      toast.success("Store deleted");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setToDelete(null);
    }
  };

  const s = summaryQuery.data || {};
  const rows = listQuery.data?.items || [];
  const noClients = !clientsQuery.isLoading && clients.length === 0;

  const columns = [
    {
      key: "store_name",
      header: "Store",
      render: (r) => (
        <div>
          <p className="font-medium text-foreground">{r.store_name}</p>
          <p className="text-xs text-muted-foreground">{r.store_id}</p>
        </div>
      ),
    },
    { key: "marketplace", header: "Marketplace", render: (r) => <Chip label={r.marketplace} tone="blue" /> },
    { key: "client", header: "Client", render: (r) => clientMap[r.client_id] || <span className="text-muted-foreground">—</span> },
    {
      key: "agency",
      header: "VA Agency",
      render: (r) => agencyMap[r.assigned_va_agency_id] || <span className="text-muted-foreground">Unassigned</span>,
    },
    {
      key: "status",
      header: "Status",
      render: (r) => <StatusBadge label={r.status === "active" ? "Active" : "Inactive"} tone={r.status === "active" ? "green" : "neutral"} />,
    },
    { key: "created", header: "Created", render: (r) => formatRelativeTime(r.created_at) },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (r) => (
        writable ? (
          <RowActions
            items={[
              { key: "edit", label: "Edit", icon: Pencil, onSelect: () => openEdit(r) },
              { key: "delete", label: "Delete", icon: Trash2, destructive: true, separatorBefore: true, onSelect: () => setToDelete(r) },
            ]}
          />
        ) : null
      ),
    },
  ];

  return (
    <div className="space-y-6" data-testid="page-stores">
      <PageHeader
        sticky
        title="Stores"
        description="Connect and manage marketplace and storefront integrations."
        actions={
          <div className="flex items-center gap-2">
            <ExportButton filename="stores" rows={rows} testId="stores-export" columns={[
              { label: "Store", key: "store_name" },
              { label: "Store ID", key: "store_id" },
              { label: "Marketplace", key: "marketplace" },
              { label: "Client", get: (r) => clientMap[r.client_id] || "" },
              { label: "VA Agency", get: (r) => agencyMap[r.assigned_va_agency_id] || "" },
              { label: "Status", key: "status" },
              { label: "Created", key: "created_at" },
            ]} />
            {writable && (
              <Button className="gap-2" onClick={openCreate} disabled={noClients} data-testid="stores-primary-action">
                <Plus className="h-4 w-4" />
                New Store
              </Button>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard testId="stat-total-stores" label="Total Stores" value={s.total ?? "—"} icon={StoreIcon} tone="blue" />
        <StatCard testId="stat-active-stores" label="Active Stores" value={s.active ?? "—"} icon={CheckCircle2} tone="green" />
        <StatCard testId="stat-inactive-stores" label="Inactive Stores" value={s.inactive ?? "—"} icon={PauseCircle} tone="neutral" />
        <StatCard testId="stat-unassigned-stores" label="Unassigned Stores" value={s.unassigned ?? "—"} icon={Unlink} tone="amber" />
      </div>

      <FilterShell
        onApply={() => { setApplied(draft); toast.success("Filters applied"); }}
        onReset={() => { setDraft(DEFAULT_FILTERS); setApplied(DEFAULT_FILTERS); toast("Filters reset"); }}
      >
        <SearchInput value={draft.search} onChange={(v) => setDraft((d) => ({ ...d, search: v }))} placeholder="Search stores…" />
        <Select value={draft.status} onValueChange={(v) => setDraft((d) => ({ ...d, status: v }))}>
          <SelectTrigger className="h-10 bg-background" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <Select value={draft.marketplace} onValueChange={(v) => setDraft((d) => ({ ...d, marketplace: v }))}>
          <SelectTrigger className="h-10 bg-background" data-testid="filter-marketplace"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All marketplaces</SelectItem>
            {MARKETPLACES.map((m) => (<SelectItem key={m} value={m}>{m}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={draft.client} onValueChange={(v) => setDraft((d) => ({ ...d, client: v }))}>
          <SelectTrigger className="h-10 bg-background" data-testid="filter-client"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All clients</SelectItem>
            {clients.map((c) => (<SelectItem key={c.id} value={c.id}>{c.client_name}</SelectItem>))}
          </SelectContent>
        </Select>
      </FilterShell>

      {listQuery.isLoading ? (
        <TableSkeleton rows={6} cols={6} />
      ) : (
        <DataTable
          testId="stores-table"
          paginated
          columns={columns}
          data={rows}
          emptyState={
            <EmptyState
              icon={StoreIcon}
              title={noClients ? "Add a client first" : "No stores connected"}
              description={noClients ? "Every store must belong to a client. Create a client, then connect their stores." : "Connect a Shopify, Amazon, or custom store to start syncing orders."}
              testId="stores-empty-state"
              action={
                writable ? (
                  <Button className="gap-2" onClick={openCreate} disabled={noClients} data-testid="stores-empty-action">
                    <Plus className="h-4 w-4" /> New Store
                  </Button>
                ) : null
              }
            />
          }
        />
      )}

      <StoreDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        record={editing}
        clients={clients}
        agencies={agencies}
        vaStaff={vaStaff}
        onSaved={refresh}
      />
      <ConfirmDialog
        open={!!toDelete}
        onOpenChange={(o) => !o && setToDelete(null)}
        title="Delete store?"
        description={`This permanently removes "${toDelete?.store_name}" and is recorded in the Activity Log.`}
        confirmLabel="Delete store"
        destructive
        onConfirm={confirmDelete}
      />
    </div>
  );
}
